import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { faInfoCircle as faInfoCircleSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useRef } from 'react';
import { DomainGq } from '../../../graphql/projects/models/DomainGq';
import { ProjectKeywordGq } from '../../../graphql/projects/models/ProjectKeywordGq';
import { SerpResultGq } from '../../../graphql/projects/models/SerpResultGq';
import { formatNumber } from '../../../logic/otherUtilities/formatting/formatNumber';
import VoxelText from '../../voxel/Text/VoxelText';
import VoxelTooltipShort from '../../voxel/Tooltip/VoxelTooltipShort';
import VoxelLink from '../../voxel/Link/VoxelLink';
import VoxelChip from '../../voxel/Chips/VoxelChip';
import { DomainTypeGq } from '../../../graphql/projects/models/DomainTypeGq';
import { ProjectGq } from '../../../graphql/projects/models/ProjectGq';

export interface SerpTableProps {
  project: ProjectGq;
  keyword: ProjectKeywordGq;
  serpResult: SerpResultGq[];
  rankNumber: number;
}

export const SerpTable: React.FC<SerpTableProps> = ({
  project,
  keyword,
  serpResult,
  rankNumber,
}) => {
  // Rank scroll logic
  const rankRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!!rankRef.current) {
      rankRef.current.style.position = 'relative';
      setTimeout(() => rankRef.current.scrollIntoView({ block: 'center' }), 0);
    }
  }, [rankRef.current]);

  return (
    <div className="max-w-[700px]">
      {serpResult.map((serpRes) => {
        if (!serpRes || !serpRes.url) return null;

        const rankSelected = rankNumber === serpRes.rankCombined;
        const traffic = formatNumber((serpRes.ctr * keyword.volume) / 100);
        const domainRank = keyword.domainRanks?.find((dr) => dr.rank === serpRes.rankCombined);
        const domain = !!domainRank
          ? project.domains
              .filter((d) => d.type !== DomainTypeGq.AUTOMATIC)
              .find((d) => d.id === domainRank.domainId)
          : undefined;
        const breadcrumb = serpRes.breadcrumb?.length ? serpRes.breadcrumb : serpRes.url;
        const breadcrumbDomain = breadcrumb?.slice(
          0,
          breadcrumb?.indexOf(serpRes?.domain) + serpRes.domain?.length
        );
        const breadcrumbTail = breadcrumb
          ?.slice(breadcrumb.indexOf(serpRes.domain) + serpRes.domain?.length, 1000)
          .replaceAll('/', ' › ');

        return (
          <div
            key={serpRes.id}
            className={`
            flex flex-row flex-nowrap items-center 
                ${rankSelected ? 'rounded-xl bg-voxel-gray-50' : ''}
                `}
          >
            {/* left column */}
            <div className=" grow-0 text-center p-4">
              <VoxelTooltipShort
                message={`Rank #${
                  serpRes.rankCombined
                } yields an estimated ${traffic} clicks (${formatNumber(serpRes.ctr, 2)}% CTR)`}
              >
                <div ref={rankSelected ? rankRef : undefined}>
                  <VoxelText variant={'title3'} color={rankSelected ? 'dark' : 'light'}>
                    #{serpRes.rankCombined}
                  </VoxelText>
                </div>
              </VoxelTooltipShort>
            </div>
            {/* right column */}
            <div className="grow flex flex-col gap-1 p-4 text-left">
              <div className={'flex flex-row items-center justify-start gap-2'}>
                {serpRes.type === 'featured_snippet' && (
                  <div
                    className={`${rankSelected ? 'text-voxel-orange-400' : 'text-voxel-gray-400'}`}
                  >
                    <VoxelTooltipShort message="featured snippet">
                      <FontAwesomeIcon
                        icon={rankSelected ? faInfoCircleSolid : faInfoCircle}
                        size="xl"
                      />
                    </VoxelTooltipShort>
                  </div>
                )}
                {!!domain && (
                  <VoxelChip
                    title={domain.name}
                    size="sm"
                    color={domain.type === DomainTypeGq.PROJECT ? 'success' : 'neutral'}
                  />
                )}
                <div className="flex flex-row gap-1 flex-nowrap w-full">
                  <span className={'text-xs font-medium text-voxel-gray-500'}>
                    {breadcrumbDomain}
                  </span>
                  <span className={'w-fit text-xs font-medium text-voxel-gray-500 font-sans'}>
                    {breadcrumbTail}
                  </span>
                </div>
              </div>
              <VoxelLink href={serpRes.url} openInNewIcon openInNewTab className="text-ellipsis ">
                {serpRes.title}
              </VoxelLink>
              <VoxelText variant="bread-normal" className="!text-xs">
                {serpRes.description}
              </VoxelText>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SerpTable;
